import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { take } from 'rxjs';
import { Attachment } from '../../models/submissions/attachment';
import { MediaUtils } from '../../utils/media-utils';
import { AttachmentUtils } from '../../utils/attachment-utils';
import { Asset } from '../../models/asset/dto/asset';
import { AssetSize } from '../../models/enum/dto/asset-size.enum';

/**
 * Downloads and previews files that live behind presigned URLs (Attachment) or are still local
 * to the browser session (File). Centralizes the blob-fetch + save-dialog plumbing that every
 * file list consumer would otherwise have to duplicate.
 */
@Injectable({ providedIn: 'root' })
export class FileDownloadService {
  private readonly http = inject(HttpClient);

  /** Fetches the attachment's presigned URL as a blob and triggers the browser save dialog. */
  public downloadAttachment(attachment: Attachment): void {
    const presignedUrl = AttachmentUtils.getReadableUrl(attachment);
    if (!presignedUrl) {
      return;
    }
    this.http
      .get(presignedUrl, { responseType: 'blob' })
      .pipe(take(1))
      .subscribe(blob => MediaUtils.downloadBlob(blob, attachment.fileName));
  }

  public downloadAsset(asset: Asset): void {
    const url = AttachmentUtils.getReadableAssetUrl(asset, [
      AssetSize.Original,
      AssetSize.Large,
      AssetSize.Medium,
      AssetSize.Small,
      AssetSize.Thumb
    ]);
    if (!url) {
      return;
    }
    this.http
      .get(url, { responseType: 'blob' })
      .pipe(take(1))
      .subscribe(blob => MediaUtils.downloadBlob(blob, asset.fileName));
  }

  /** Triggers the browser save dialog for a file that is still local to this session. */
  public downloadLocalFile(file: File): void {
    MediaUtils.downloadBlob(file, file.name);
  }

  /** Opens a local file in a new browser tab. */
  public previewLocalFile(file: File): void {
    MediaUtils.openFileInNewTab(file);
  }
}
